/**
 * 历史栈 —— 命令的 execute / undo / redo / 合并 / 预算淘汰。
 *
 * 设计取舍：
 *  · 只认 Patch，不理解业务语义（ops 负责把动作翻译成 Patch）
 *  · 合并窗口 + mergeKey 让「拖一次 / 滑杆连按」= 1 条 undo，而不是几十条
 *  · 双重预算（栈深 50 + 内存 32MB），防止超大文档把内存吃穿
 *  · 结构变动后 redo 分支必须作废（这是所有编辑器都遵守的语义）
 */
import { applyPatch, patchBytes, revertPatch, sameTarget, type Patch } from './patch';

export interface Command {
  label: string;
  patches: Patch[];
  /** 相同 mergeKey 且在合并窗口内的相邻命令会合并为一条 */
  mergeKey?: string;
  time?: number;
}

export interface HistoryOptions {
  maxDepth?: number;
  maxBytes?: number;
  mergeWindowMs?: number;
}

const DEFAULT_MAX_DEPTH = 50;
const DEFAULT_MAX_BYTES = 32 * 1024 * 1024;
const DEFAULT_MERGE_WINDOW_MS = 300;

export const commandBytes = (c: Command): number =>
  c.patches.reduce((sum, p) => sum + patchBytes(p), 0);

export class History {
  private readonly root: Element;
  private readonly past: Command[] = [];
  private readonly future: Command[] = [];
  private readonly maxDepth: number;
  private readonly maxBytes: number;
  private readonly mergeWindowMs: number;
  private readonly listeners = new Set<() => void>();
  private bytes = 0;

  constructor(root: Element, opts: HistoryOptions = {}) {
    this.root = root;
    this.maxDepth = opts.maxDepth ?? DEFAULT_MAX_DEPTH;
    this.maxBytes = opts.maxBytes ?? DEFAULT_MAX_BYTES;
    this.mergeWindowMs = opts.mergeWindowMs ?? DEFAULT_MERGE_WINDOW_MS;
  }

  get canUndo(): boolean {
    return this.past.length > 0;
  }
  get canRedo(): boolean {
    return this.future.length > 0;
  }
  /** 可撤销步数 */
  get length(): number {
    return this.past.length;
  }
  get byteSize(): number {
    return this.bytes;
  }
  get undoLabel(): string | null {
    return this.past[this.past.length - 1]?.label ?? null;
  }
  get redoLabel(): string | null {
    return this.future[this.future.length - 1]?.label ?? null;
  }

  /** 返回 false 表示没有实际改动（空 patch 一律丢弃，绝不污染历史） */
  execute(cmd: Command): boolean {
    if (cmd.patches.length === 0) return false;
    for (const p of cmd.patches) applyPatch(this.root, p);
    this.future.length = 0;

    const top = this.past[this.past.length - 1];
    const time = cmd.time ?? Date.now();
    if (top && cmd.mergeKey && top.mergeKey === cmd.mergeKey && time - (top.time ?? 0) < this.mergeWindowMs) {
      this.mergeInto(top, cmd, time);
    } else {
      const entry: Command = { ...cmd, patches: [...cmd.patches], time };
      this.past.push(entry);
      this.bytes += commandBytes(entry);
    }
    this.evict();
    this.emit();
    return true;
  }

  undo(): boolean {
    const cmd = this.past.pop();
    if (!cmd) return false;
    for (let i = cmd.patches.length - 1; i >= 0; i--) revertPatch(this.root, cmd.patches[i] as Patch);
    this.future.push(cmd);
    this.bytes -= commandBytes(cmd);
    this.emit();
    return true;
  }

  redo(): boolean {
    const cmd = this.future.pop();
    if (!cmd) return false;
    for (const p of cmd.patches) applyPatch(this.root, p);
    this.past.push(cmd);
    this.bytes += commandBytes(cmd);
    this.emit();
    return true;
  }

  clear(): void {
    this.past.length = 0;
    this.future.length = 0;
    this.bytes = 0;
    this.emit();
  }

  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private mergeInto(top: Command, cmd: Command, time: number): void {
    this.bytes -= commandBytes(top);
    for (const np of cmd.patches) {
      const hit = top.patches.find((q) => sameTarget(q, np));
      if (hit?.k === 'attr' && np.k === 'attr') hit.after = np.after; // before 始终保留最早值
      else if (hit?.k === 'container' && np.k === 'container') hit.after = np.after;
      else top.patches.push(np);
    }
    top.time = time;
    this.bytes += commandBytes(top);
  }

  private evict(): void {
    while (this.past.length > this.maxDepth || this.bytes > this.maxBytes) {
      const dropped = this.past.shift();
      if (!dropped) break;
      this.bytes -= commandBytes(dropped);
    }
  }

  private emit(): void {
    for (const cb of this.listeners) cb();
  }
}
